import React, { useState } from "react";
import { Table, Card, Button, Select, Tag, Avatar } from "antd";
import { Helmet } from "react-helmet-async";
import Layout from "../../Layout";
import { 
  Bell, 
  Clock, 
  Download, 
  TrendingUp,
  TrendingDown,
  Globe,
  Phone,
  Share2
} from "lucide-react";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LineChart,
  Line
} from "recharts";

// Dữ liệu mẫu cho Biểu đồ xu hướng ở đáy khối Phễu (Inbox vs Leads)
const hourlyChartData = [
  { time: "08:00", inbox: 45, leads: 12 },
  { time: "09:00", inbox: 95, leads: 28 },
  { time: "10:00", inbox: 140, leads: 45 },
  { time: "11:00", inbox: 110, leads: 32 },
  { time: "12:00", inbox: 60, leads: 15 },
  { time: "13:00", inbox: 85, leads: 22 },
  { time: "14:00", inbox: 165, leads: 58 },
  { time: "15:00", inbox: 130, leads: 40 },
];

// Dữ liệu Phễu chuyển đổi (Tiến trình chuyển đổi Pipeline - Chuẩn theo Hình 3)
const funnelData = [
  { label: "Chưa tiếp cận", count: "1,500", percent: "100%", bgBar: "bg-blue-600", width: "100%", textColor: "text-white" },
  { label: "Tò mò, nhận thức kém", count: "1,200", percent: "80.0%", bgBar: "bg-blue-500", width: "80%", textColor: "text-gray-900" },
  { label: "Tìm hiểu sơ bộ", count: "850", percent: "56.6%", bgBar: "bg-blue-400", width: "56.6%", textColor: "text-gray-900" },
  { label: "Có nhu cầu rõ ràng nhưng còn rào cản", count: "500", percent: "33.3%", bgBar: "bg-indigo-300", width: "33.3%", textColor: "text-gray-900" },
  { label: "Nhu cầu cao, cân nhắc lựa chọn", count: "320", percent: "21.3%", bgBar: "bg-indigo-400", width: "21.3%", textColor: "text-white" },
  { label: "Liên hệ CSKH", count: "210", percent: "14.0%", bgBar: "bg-indigo-500", width: "14%", textColor: "text-white" },
  { label: "Đã đặt lịch", count: "145", percent: "9.6%", bgBar: "bg-emerald-400", width: "9.6%", textColor: "text-gray-900" },
  { label: "Xác nhận lịch hẹn", count: "90", percent: "6.0%", bgBar: "bg-emerald-500", width: "6%", textColor: "text-white" },
];

// Dữ liệu Lý do rớt (Dạng danh sách progress)
const dropReasonData = [
  { name: "Sai thông tin", value: 150, percent: 71 },
  { name: "Khách đặt lịch chưa tới", value: 40, percent: 19 },
  { name: "Khách đến chưa có nhu cầu", value: 12, percent: 6 },
  { name: "Rớt do bệnh lý", value: 8, percent: 4 },
];

export default function RealtimeDashboard() {
  const [isDark] = useState(false);

  // Render Icon Kênh MKT chuẩn nhận diện
  const renderChannelIcon = (channel: string) => {
    switch (channel.toLowerCase()) {
      case "facebook":
        return <span className="w-5 h-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold">f</span>;
      case "tiktok":
        return <span className="w-5 h-5 rounded-full bg-black text-white flex items-center justify-center text-[10px] font-bold">🎵</span>;
      case "zalo":
        return <span className="w-5 h-5 rounded-full bg-blue-500 text-white flex items-center justify-center text-[9px] font-bold">Zalo</span>;
      case "website":
        return <Globe className="w-4 h-4 text-emerald-600" />;
      case "hotline":
        return <Phone className="w-4 h-4 text-red-500" />;
      default:
        return <Share2 className="w-4 h-4 text-gray-500" />;
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Dashboard Trực Page - Realtime</title>
      </Helmet>
      
      <div className={isDark ? "dark min-h-screen" : "min-h-screen"}>
        
        {/* HEADER TỔNG KHANH & BỘ LỌC */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
          <div>
            <h1 className="text-xl font-bold text-000000e0 flex items-center gap-2">
              Dashboard Trực Page - Realtime
              <span className="bg-emerald-500/10 text-emerald-600 text-xs px-2.5 py-0.5 rounded-full border border-emerald-200 flex items-center gap-1">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping"></span> Live
              </span>
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button icon={<Download className="w-4 h-4" />}>Xuất báo cáo</Button>
            <div className="relative">
              <Button icon={<Bell className="w-4 h-4" />} />
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] w-4 h-4 rounded-full flex items-center justify-center font-bold">3</span>
            </div>
          </div>
        </div>

        {/* BỘ LỌC HÀNG NGANG */}
        <Card size="small" className="mb-4 shadow-sm border-slate-200 sticky top-[64px] z-[25]">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="text-[11px] text-slate-400 font-medium block mb-1">THỜI GIAN</label>
              <Select defaultValue="today" className="w-full" size="small" options={[{ value: 'today', label: 'Hôm nay' }, { value: 'week', label: 'Tuần này' }]} />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 font-medium block mb-1">CƠ SỞ</label>
              <Select defaultValue="all" className="w-full" size="small" options={[{ value: 'all', label: 'Tất cả cơ sở' }, { value: 'cs1', label: 'CS1 Quận 1' }, { value: 'cs2', label: 'CS2 Quận 7' }]} />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 font-medium block mb-1">TEAM</label>
              <Select defaultValue="all" className="w-full" size="small" options={[{ value: 'all', label: 'Tất cả Team' }]} />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 font-medium block mb-1">NHÂN VIÊN</label>
              <Select defaultValue="all" className="w-full" size="small" options={[{ value: 'all', label: 'Tất cả nhân viên' }]} />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 font-medium block mb-1">KÊNH MKT</label>
              <Select defaultValue="all" className="w-full" size="small" options={[{ value: 'all', label: 'Tất cả kênh' }]} />
            </div>
            <div>
              <label className="text-[11px] text-slate-400 font-medium block mb-1">CHIẾN DỊCH</label>
              <Select defaultValue="all" className="w-full" size="small" options={[{ value: 'all', label: 'Tất cả chiến dịch' }]} />
            </div>
          </div>
        </Card>

        {/* KHỐI 6 THẺ KPI TOP */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          <Card size="small" className="border-l-4 border-l-blue-500 shadow-sm">
            <div className="text-xs text-slate-500">Tổng Inbox / Cmt</div>
            <div className="text-2xl font-bold text-000000e0 my-0.5">1,376</div>
            <div className="text-[11px] text-emerald-600 flex items-center font-medium"><TrendingUp className="w-3 h-3 mr-0.5"/> 12%</div>
          </Card>
          <Card size="small" className="border-l-4 border-l-indigo-500 shadow-sm">
            <div className="text-xs text-slate-500">Khách hàng mới</div>
            <div className="text-2xl font-bold text-000000e0 my-0.5">353</div>
            <div className="text-[11px] text-emerald-600 flex items-center font-medium"><TrendingUp className="w-3 h-3 mr-0.5"/> 5%</div>
          </Card>
          <Card size="small" className="border-l-4 border-l-amber-500 shadow-sm">
            <div className="text-xs text-slate-500">Lịch hẹn</div>
            <div className="text-2xl font-bold text-000000e0 my-0.5">145</div>
            <div className="text-[11px] text-rose-500 flex items-center font-medium"><TrendingDown className="w-3 h-3 mr-0.5"/> 2%</div>
          </Card>
          <Card size="small" className="border-l-4 border-l-emerald-500 shadow-sm">
            <div className="text-xs text-slate-500">Khách đã đến</div>
            <div className="text-2xl font-bold text-000000e0 my-0.5">85</div>
            <div className="text-[11px] text-emerald-600 flex items-center font-medium"><TrendingUp className="w-3 h-3 mr-0.5"/> 8%</div>
          </Card>
          <Card size="small" className="border-l-4 border-l-red-500 shadow-sm">
            <div className="text-xs text-slate-500">Doanh thu (VNĐ)</div>
            <div className="text-lg font-bold text-000000e0 my-1">125,000,000</div>
            <div className="text-[11px] text-emerald-600 flex items-center font-medium"><TrendingUp className="w-3 h-3 mr-0.5"/> 15%</div>
          </Card>
          <Card size="small" className="border-l-4 border-l-purple-500 shadow-sm">
            <div className="text-xs text-slate-500">Tỷ lệ CVR</div>
            <div className="text-2xl font-bold text-000000e0 my-0.5">6.8%</div>
            <div className="text-[11px] text-slate-400">Từ Inbox → Đã đến</div>
          </Card>
        </div>

        {/* ROW: TỐC ĐỘ PHẢN HỒI & CẢNH BÁO SLA */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4">
          <Card size="small" className="shadow-sm md:col-span-4 flex flex-col justify-between">
            <div>
              <div className="flex justify-between items-center mb-1">
                <div>
                  <div className="text-xs font-bold text-slate-700">Tốc độ Phản hồi (ART)</div>
                  <div className="text-[10px] text-slate-400">Trung bình toàn hệ thống</div>
                </div>
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                  <Clock className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="flex items-baseline gap-2 my-2">
                <span className="text-3xl font-bold text-000000e0">3m 45s</span>
                <span className="text-xs text-emerald-600 font-medium">⚡ Nhanh hơn 15s</span>
              </div>
            </div>
            <div>
              <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                <div className="bg-blue-500 h-full w-[75%] rounded-full"></div>
              </div>
              <div className="text-right text-[10px] text-slate-400 mt-1">Mục tiêu: Dưới 5 phút</div>
            </div>
          </Card>

          <Card size="small" className="shadow-sm md:col-span-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-xs font-bold text-slate-700">Cảnh báo SLA Phản hồi (Hội thoại chờ)</span>
              <span className="text-[10px] bg-red-50 text-red-500 border border-red-200 px-2 py-0.5 rounded-full flex items-center gap-1 font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span> Live
              </span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
              <div className="bg-slate-50 p-2 rounded border border-slate-200">
                <div className="text-[11px] text-slate-500 mb-1">Tổng đang chờ</div>
                <div className="text-2xl font-bold text-000000e0">42</div>
              </div>
              <div className="bg-amber-50/60 p-2 rounded border border-amber-200">
                <div className="text-[11px] text-amber-700 mb-1">Quá 5 phút</div>
                <div className="text-2xl font-bold text-amber-600">15</div>
              </div>
              <div className="bg-orange-50/60 p-2 rounded border border-orange-200">
                <div className="text-[11px] text-orange-700 mb-1">Quá 15 phút</div>
                <div className="text-2xl font-bold text-orange-600">6</div>
              </div>
              <div className="bg-red-50/60 p-2 rounded border border-red-200">
                <div className="text-[11px] text-red-700 mb-1">Quá 30 phút</div>
                <div className="text-2xl font-bold text-red-600">2</div>
              </div>
            </div>
          </Card>
        </div>

        {/* MAIN BODY: KHỐI PHỄU CỘT TRÁI (8/12) & KHỐI CẦN XỬ LÝ CỘT PHẢI (4/12) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mb-4 items-stretch">
          
          {/* CỘT TRÁI (8/12): PHÂN LOẠI TRẠNG THÁI LEAD & PHỄU CHUYỂN ĐỔI */}
          <Card size="small" className="lg:col-span-8 shadow-sm flex flex-col justify-between">
            <div>
              {/* Header chung của block */}
              <div className="flex justify-between items-center border-b border-slate-100 pb-2 mb-4">
                <span className="font-bold text-sm text-000000e0">
                  Phân loại Trạng thái Lead & Phễu chuyển đổi
                </span>
                <Button type="link" size="small" className="text-xs p-0 text-blue-600">
                  Chi tiết →
                </Button>
              </div>

              {/* Khối chia đôi 2 phần: Pipeline bên trái & Lý do rớt bên phải */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mb-4">
                
                {/* Phần Pipeline (7/12) - Chuẩn giao diện Hình 3 */}
                <div className="md:col-span-7 space-y-1.5">
                  <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">
                    TIẾN TRÌNH CHUYỂN ĐỔI (PIPELINE)
                  </div>
                  {funnelData.map((item, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center h-8 bg-slate-50/80 rounded px-2 text-xs font-medium"
                    >
                      {/* Label bên trái */}
                      <div className="w-32 text-right pr-2 text-slate-600 text-[11px] font-medium leading-tight shrink-0 truncate">
                        {item.label}
                      </div>

                      {/* Khung chứa Bar & Số lượng bên trong */}
                      <div className="flex-1 h-6 bg-transparent rounded overflow-hidden flex items-center">
                        <div
                          className={`h-full ${item.bgBar} rounded-r-md flex items-center px-2.5 transition-all duration-300 min-w-[38px]`}
                          style={{ width: item.width }}
                        >
                          <span className={`text-[11px] font-bold ${item.textColor}`}>
                            {item.count}
                          </span>
                        </div>
                      </div>

                      {/* Tỷ lệ phần trăm ở cuối */}
                      <div className="w-12 text-right text-[11px] font-semibold text-slate-500 shrink-0">
                        {item.percent}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Phần Lý do rớt (5/12) */}
                <div className="md:col-span-5 flex flex-col justify-between border-l border-slate-100 pl-4">
                  <div>
                    <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-3">
                      LÝ DO RỚT / KHÔNG ĐẾN
                    </div>
                    <div className="space-y-3">
                      {dropReasonData.map((reason, idx) => (
                        <div key={idx} className="space-y-1">
                          <div className="flex justify-between text-xs text-slate-600">
                            <span className="truncate">{reason.name}</span>
                            <span className="font-semibold text-000000e0">{reason.value}</span>
                          </div>
                          <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                            <div 
                              className="bg-slate-400 h-full rounded-full" 
                              style={{ width: `${reason.percent}%` }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Box Tổng rớt / Hủy nằm ở đáy */}
                  <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 mt-4">
                    <div className="text-[11px] text-slate-500 font-medium">Tổng rớt / Hủy</div>
                    <div className="text-2xl font-bold text-000000e0">210</div>
                  </div>
                </div>

              </div>
            </div>

            {/* Line chart nhỏ dưới đáy phễu */}
            <div className="pt-3 border-t border-slate-100 mt-2">
              <div className="flex justify-end gap-4 text-xs mb-2">
                <span className="flex items-center gap-1.5 text-blue-600 font-medium">
                  <span className="w-2.5 h-2.5 rounded-full border-2 border-blue-600 bg-white"></span> Inbox mới
                </span>
                <span className="flex items-center gap-1.5 text-emerald-600 font-medium">
                  <span className="w-2.5 h-2.5 rounded-full border-2 border-emerald-600 bg-white"></span> Leads (SĐT)
                </span>
              </div>
              <div className="h-28 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={hourlyChartData} margin={{ top: 5, right: 10, left: -25, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="time" tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <YAxis tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                    <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '6px' }} />
                    <Line type="monotone" dataKey="inbox" stroke="#3b82f6" strokeWidth={2} dot={false} />
                    <Line type="monotone" dataKey="leads" stroke="#10b981" strokeWidth={2} strokeDasharray="4 4" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>

          </Card>

          {/* CỘT PHẢI (4/12): CẦN XỬ LÝ (Có Scrollbar đồng bộ chiều cao) */}
          <Card 
            size="small" 
            className="lg:col-span-4 shadow-sm h-[560px]" 
            styles={{ 
                body: { 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                overflow: 'hidden',
                padding: '12px'
                } 
            }}
            >
            {/* Header + Tabs */}
            <div className="shrink-0">
                <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-sm text-000000e0">Cần xử lý</span>
                <span className="text-xs text-slate-400">6 mục đang mở</span>
                </div>

                {/* Filter Tabs */}
                <div className="flex gap-1.5 mb-3 flex-wrap">
                <Button size="small" className="text-xs">Tất cả 6</Button>
                <Button size="small" type="primary" className="text-xs bg-slate-900">Nghiêm trọng 4</Button>
                <Button size="small" className="text-xs">Cảnh báo 1</Button>
                <Button size="small" className="text-xs">Lưu ý 1</Button>
                </div>
            </div>

            {/* Danh sách công việc dạng Thẻ -> THÊM min-h-0 ĐỂ FLEX CHILD CO TÃN ĐÚNG & SCROLL HOẠT ĐỘNG */}
            <div className="flex-1 min-h-0 overflow-y-auto pr-1.5 space-y-3 custom-scrollbar">
                
                {/* Thẻ 1 */}
                <div className="p-3 bg-slate-50/80 border border-slate-200 rounded-lg text-xs space-y-2">
                <div className="flex justify-between items-start">
                    <div>
                    <div className="font-bold text-slate-700">Đặng Quỳnh Như đạt SLA 52%</div>
                    <div className="text-[11px] text-slate-500">Phản hồi trung bình 8,8 phút, 4 khách đang chờ.</div>
                    </div>
                    <span className="text-[10px] text-rose-500 font-medium shrink-0">⏰ Chờ 9 phút</span>
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                    <span className="flex items-center gap-1"><span className="text-blue-600 font-bold">f</span> Fanpage Mỹ Phẩm</span>
                    <span>• CS2 Quận 7</span>
                </div>
                <div className="bg-white p-2 rounded border border-slate-200 text-slate-600">
                    <div className="font-semibold text-slate-700">KH Nguyễn An</div>
                    <div className="italic text-slate-600 mt-0.5">"Cho mình xin bảng giá chi tiết liệu trình với ạ..."</div>
                </div>
                <div className="flex gap-2 pt-1">
                    <Button size="small" className="text-xs font-medium">Nhắc nhân viên</Button>
                    <Button size="small" type="text" className="text-xs text-slate-500">Đã xử lý</Button>
                </div>
                </div>

                {/* Thẻ 2 */}
                <div className="p-3 bg-slate-50/80 border border-slate-200 rounded-lg text-xs space-y-2">
                <div className="flex justify-between items-start">
                    <div>
                    <div className="font-bold text-red-600 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span> Nghiêm trọng
                    </div>
                    <div className="font-bold text-slate-700 mt-1">Lê Khánh Vy đạt SLA 52%</div>
                    <div className="text-[11px] text-slate-500">Phản hồi trung bình 7,7 phút, 4 khách đang chờ.</div>
                    </div>
                    <span className="text-[10px] text-slate-400 shrink-0">31 phút trước</span>
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                    <span>🎵 TikTok Official</span>
                    <span>• CS1 Quận 1</span>
                </div>
                <div className="bg-white p-2 rounded border border-slate-200 text-slate-600">
                    <div className="font-semibold text-slate-500 flex justify-between">
                    <span>KH Trần Bình</span>
                    <span className="text-rose-500 font-normal text-[10px]">⏰ Chờ 12 phút</span>
                    </div>
                    <div className="italic text-slate-600 mt-0.5">"Dạ shop đi check inbox em gửi ảnh da với nha..."</div>
                </div>
                <div className="flex gap-2 pt-1">
                    <Button size="small" className="text-xs font-medium">Nhắc nhân viên</Button>
                    <Button size="small" type="text" className="text-xs text-slate-500">Đã xử lý</Button>
                </div>
                </div>

                {/* Thẻ 3 */}
                <div className="p-3 bg-slate-50/80 border border-slate-200 rounded-lg text-xs space-y-2">
                <div className="flex justify-between items-start">
                    <div>
                    <div className="font-bold text-red-600 flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full bg-red-500"></span> Nghiêm trọng
                    </div>
                    <div className="font-bold text-slate-700 mt-1">6 hội thoại chờ quá 15 phút</div>
                    <div className="text-[11px] text-slate-500">Khách đã nhắn nhưng chưa có phản hồi đầu tiên.</div>
                    </div>
                    <span className="text-[10px] text-slate-400 shrink-0">35 phút trước</span>
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                    <span>⚙ Tất cả cơ sở</span>
                </div>
                <div className="flex gap-2 pt-1">
                    <Button size="small" className="text-xs font-medium">Điều phối lại</Button>
                    <Button size="small" type="text" className="text-xs text-slate-500">Bỏ qua</Button>
                </div>
                </div>

            </div>

            {/* Footer Card */}
            <div className="pt-2 text-right text-[10px] text-slate-400 border-t border-slate-100 mt-2 shrink-0">
                Cập nhật 1 phút trước
            </div>
            </Card>

        </div>

        {/* BẢNG 1: HIỆU QUẢ THEO KÊNH (CÓ ICON LOGO KÊNH) */}
        <Card size="small" title="Hiệu quả theo Kênh (Channel Performance)" className="shadow-sm mb-4">
          <Table
            scroll={{ x: "max-content" }}
            dataSource={[
              { key: "1", nguon: "Facebook", inbox: 1245, khach: 320, lichhen: 145, den: 85, mua: 52, conversion: "4.1%" },
              { key: "2", nguon: "TikTok", inbox: 850, khach: 180, lichhen: 65, den: 30, mua: 12, conversion: "1.4%" },
              { key: "3", nguon: "Zalo", inbox: 420, khach: 210, lichhen: 120, den: 85, mua: 45, conversion: "10.7%" },
              { key: "4", nguon: "Website", inbox: 215, khach: 95, lichhen: 45, den: 25, mua: 18, conversion: "8.3%" },
              { key: "5", nguon: "Hotline", inbox: 150, khach: 135, lichhen: 90, den: 75, mua: 50, conversion: "33.3%" },
            ]}
            pagination={false}
            size="small"
            columns={[
              { 
                title: "NGUỒN KÊNH", 
                dataIndex: "nguon", 
                key: "nguon", 
                render: (v) => (
                  <div className="flex items-center gap-2 font-semibold text-000000e0">
                    {renderChannelIcon(v)}
                    <span>{v}</span>
                  </div>
                ) 
              },
              { title: "INBOX", dataIndex: "inbox", key: "inbox", render: (v) => <span className="text-000000e0 font-medium">{v}</span> },
              { title: "KHÁCH", dataIndex: "khach", key: "khach" },
              { title: "LỊCH HẸN", dataIndex: "lichhen", key: "lichhen" },
              { title: "ĐẾN", dataIndex: "den", key: "den" },
              { title: "MUA", dataIndex: "mua", key: "mua", render: (v) => <span className="font-bold text-emerald-600">{v}</span> },
              { title: "CONVERSION", dataIndex: "conversion", key: "conversion", render: (v) => <Tag color="green">{v}</Tag> },
            ]}
          />
        </Card>

        {/* BẢNG 2: CHIẾN DỊCH CAMPAIGN */}
        <Card size="small" title="Hiệu quả & Chi phí theo Chiến dịch (Campaigns)" className="shadow-sm mb-4">
          <Table
            scroll={{ x: "max-content" }}
            dataSource={[
              { key: "1", tenchiendich: "Sale T9 - Back to school", chiphi: "15,000,000", inbox: 450, khachhang: 120, giakhoang: "125,000", doanhthu: "45,000,000", roas: "3.00" },
              { key: "2", tenchiendich: "Khai trương CS3", chiphi: "25,000,000", inbox: 600, khachhang: 180, giakhoang: "138,888", doanhthu: "80,000,000", roas: "3.20" },
              { key: "3", tenchiendich: "Sinh nhật công ty", chiphi: "8,500,000", inbox: 250, khachhang: 70, giakhoang: "121,428", doanhthu: "15,000,000", roas: "1.76" },
              { key: "total", tenchiendich: "TỔNG CỘNG", chiphi: "48,500,000", inbox: 1300, khachhang: 370, giakhoang: "131,081", doanhthu: "140,000,000", roas: "2.88" }
            ]}
            pagination={false}
            size="small"
            columns={[
              { title: "CHIẾN DỊCH", dataIndex: "tenchiendich", key: "tenchiendich", render: (v, r) => r.key === 'total' ? <strong>{v}</strong> : <span className="text-blue-600 font-medium">{v}</span> },
              { title: "CHI PHÍ (VNĐ)", dataIndex: "chiphi", key: "chiphi" },
              { title: "INBOX", dataIndex: "inbox", key: "inbox" },
              { title: "KHÁCH HÀNG", dataIndex: "khachhang", key: "khachhang" },
              { title: "CPA (GIÁ/KHÁCH)", dataIndex: "giakhoang", key: "giakhoang", render: (v) => <span className="text-amber-600">{v}</span> },
              { title: "DOANH THU", dataIndex: "doanhthu", key: "doanhthu", render: (v) => <span className="text-emerald-600 font-bold">{v}</span> },
              { title: "ROAS", dataIndex: "roas", key: "roas", render: (v) => <Tag color="purple">{v}</Tag> },
            ]}
          />
        </Card>

        {/* BẢNG 3: BẢNG HIỆU SUẤT NHÂN VIÊN */}
        <Card size="small" title="Hiệu suất Nhân viên Trực Page" className="shadow-sm">
          <Table
            dataSource={[
              { key: "1", nhanvien: "Lê Thị A", status: "Online", inbox: 345, daxuly: 330, chuaxuly: 15, tgphanhoitb: "2p 15s", lichhen: 45, khachden: 28, chuyendoi: "8.1%" },
              { key: "2", nhanvien: "Trần B", status: "Online", inbox: 280, daxuly: 275, chuaxuly: 5, tgphanhoitb: "4p 30s", lichhen: 32, khachden: 18, chuyendoi: "6.4%" },
              { key: "3", nhanvien: "Hoàng C", status: "Online", inbox: 412, daxuly: 380, chuaxuly: 32, tgphanhoitb: "12p 40s", lichhen: 24, khachden: 12, chuyendoi: "2.9%" },
            ]}
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            columns={[
              { 
                title: "NHÂN VIÊN", 
                dataIndex: "nhanvien", 
                key: "nhanvien",
                render: (text, record) => (
                  <div className="flex items-center gap-2">
                    <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>{text[0]}</Avatar>
                    <div>
                      <div className="font-semibold text-000000e0">{text}</div>
                      <div className="text-[10px] text-emerald-600">● {record.status}</div>
                    </div>
                  </div>
                )
              },
              { title: "INBOX", dataIndex: "inbox", key: "inbox", render: (v) => <span className="text-blue-600 font-bold">{v}</span> },
              { title: "ĐÃ XỬ LÝ", dataIndex: "daxuly", key: "daxuly" },
              { title: "CHƯA XỬ LÝ", dataIndex: "chuaxuly", key: "chuaxuly", render: (v) => v > 0 ? <Tag color="volcano">{v}</Tag> : <Tag color="default">0</Tag> },
              { title: "TG PHẢN HỒI TB", dataIndex: "tgphanhoitb", key: "tgphanhoitb" },
              { title: "LỊCH HẸN", dataIndex: "lichhen", key: "lichhen" },
              { title: "KHÁCH ĐẾN", dataIndex: "khachden", key: "khachden" },
              { title: "CHUYỂN ĐỔI", dataIndex: "chuyendoi", key: "chuyendoi", render: (v) => <Tag color="blue">{v}</Tag> },
            ]}
          />
        </Card>

      </div>
    </Layout>
  );
}